<template>
    <div class="video-jitsi-container" :class="{'video-jitsi-fullscreen': isFullScreen, 'video-jitsi-started': isStarted}">
        <div class="video-jitsi" id="videoJitsiFrame" :class="{'jitsi-moderator':isModerator, 'jitsi-viewer': isViewer}">
            <button v-if="!isStarted" @click="startVideo" title="Start video" class="btn btn-info">
                <span><i class="icon-facetime-video icon-white"></i> Start</span>
            </button>

            <div v-if="error" class="video-jitsi-error alert alert-error">
                <b>Sorry!</b> You do not have permission to take part in this video.
                Please check with your event manager.
            </div>

            <!--
            <div v-if="isModerator" class="jitsi-note jitsi-note-moderator">
            You are a <b>Moderator</b> and can record/manage this video.</div>
            <div v-if="isViewer" class="jitsi-note jitsi-note-viewer">
            You are a <b>Viewer</b> and cannot share your own camera/mic.
            </div>
            -->
        </div>
        <div class="video-jitsi-footer">
            <ToggleFullScreen :on="isFullScreen" @click="doToggleFullScreen" />
            <div v-if="isModerator" class="jitsi-note-moderator">
            You are a <b>Moderator</b> and can record/manage this video.</div>
            <div v-if="isViewer" class="jitsi-note-viewer">
            You are a <b>Viewer</b> and cannot share your own camera/mic.
            </div>
        </div>
    </div>
</template>

<script>
import {mapGetters} from 'vuex';
import EventService from "@/services/EventService";
import ToggleFullScreen from "@/components/btn/ToggleFullScreen"

const notificationsView = [
  'connection.CONNFAIL', // shown when the connection fails,
  'dialog.cameraNotSendingData', // shown when there's no feed from user's camera
  'dialog.kickTitle', // shown when user has been kicked
  'dialog.liveStreaming', // livestreaming notifications (pending, on, off, limits)
  'dialog.lockTitle', // shown when setting conference password fails
  'dialog.maxUsersLimitReached', // shown when maximmum users limit has been reached
  'dialog.micNotSendingData', // shown when user's mic is not sending any audio
  'dialog.passwordNotSupportedTitle', // shown when setting conference password fails due to password format
  'dialog.recording', // recording notifications (pending, on, off, limits)
  'dialog.remoteControlTitle', // remote control notifications (allowed, denied, start, stop, error)
  'dialog.reservationError',
  'dialog.serviceUnavailable', // shown when server is not reachable
  'dialog.sessTerminated', // shown when there is a failed conference session
  'dialog.sessionRestarted', // show when a client reload is initiated because of bridge migration
  'dialog.tokenAuthFailed', // show when an invalid jwt is used
  'dialog.transcribing', // transcribing notifications (pending, off)
  'dialOut.statusMessage', // shown when dial out status is updated.
  'liveStreaming.busy', // shown when livestreaming service is busy
  'liveStreaming.failedToStart', // shown when livestreaming fails to start
  'liveStreaming.unavailableTitle', // shown when livestreaming service is not reachable
  'lobby.joinRejectedMessage', // shown when while in a lobby, user's request to join is rejected
  'lobby.notificationTitle', // shown when lobby is toggled and when join requests are allowed / denied
  'localRecording.localRecording', // shown when a local recording is started
  'notify.disconnected', // shown when a participant has left
  'notify.grantedTo', // shown when moderator rights were granted to a participant
  'notify.invitedOneMember', // shown when 1 participant has been invited
  'notify.invitedThreePlusMembers', // shown when 3+ participants have been invited
  'notify.invitedTwoMembers', // shown when 2 participants have been invited
  'notify.kickParticipant', // shown when a participant is kicked
  'notify.mutedRemotelyTitle', // shown when user is muted by a remote party
  'notify.mutedTitle', // shown when user has been muted upon joining,
  'notify.newDeviceAudioTitle', // prompts the user to use a newly detected audio device
  'notify.newDeviceCameraTitle', // prompts the user to use a newly detected camera
  'notify.passwordRemovedRemotely', // shown when a password has been removed remotely
  'notify.passwordSetRemotely', // shown when a password has been set remotely
  'notify.raisedHand', // shown when a partcipant used raise hand,
  'notify.startSilentTitle', // shown when user joined with no audio
  'prejoin.errorDialOut',
  'prejoin.errorDialOutDisconnected',
  'prejoin.errorDialOutFailed',
  'prejoin.errorDialOutStatus',
  'prejoin.errorStatusCode',
  'prejoin.errorValidation',
  'recording.busy', // shown when recording service is busy
  'recording.failedToStart', // shown when recording fails to start
  'recording.unavailableTitle', // shown when recording service is not reachable
  'toolbar.noAudioSignalTitle', // shown when a broken mic is detected
  'toolbar.noisyAudioInputTitle', // shown when noise is detected for the current microphone
//  'toolbar.talkWhileMutedPopup', // shown when user tries to speak while muted
  'transcribing.failedToStart' // shown when transcribing fails to start
];

export default {
    name: "VideoJitsi",
    props: {
        meeting_id: {
            type: Number,
            default: null,
        },
        appointment_id: {
            type: Number,
            default: null,
        },
        attendee_id: {
            type: Number,
            default: null,
        },
        start_now: {
            type: Boolean,
            default: false,
        }
    },
    components: {
        ToggleFullScreen,
    },
    data() {
        return {
            isStarted: false,
            isModerator: false,
            isViewer: false,
            isFullScreen: false,
            jitsiAPI: null,
            jitsiConnectionId: null, // Assigned by Jitsi videoConferenceJoined { id: '7b23d4e1' ... }
            room: null,
            error: null,
        }
    },
    computed: {
        ...mapGetters({
            me: 'me/currentAttendee',
        }),
    },
    methods: {
        startVideo() {
            return EventService.getJitsiToken({
                meeting_id: this.meeting_id,
                appointment_id: this.appointment_id,
                attendee_id: this.attendee_id
            })
                .then(resp => {
                    console.log("getJitsiToken:", resp);
                    const jwt = resp.data.token
                    const payload = JSON.parse(atob(jwt.split('.')[1]));
                    console.log('Payload:', payload);
                    const domain = '8x8.vc';
                    const roomName = payload.sub + "/" + payload.room; // Full namespaced room name
                    this.room = payload.room; // room name within MM a.k.a. resource_name
                    this.isModerator = payload.context.user.moderator ? payload.context.user.moderator === 'true': false;
                    this.isViewer = payload.context.user.viewer ? payload.context.user.viewer === 'true' : false;

                    // https://github.com/jitsi/jitsi-meet/blob/master/interface_config.js#L204
                    const buttons = [
                        'embedmeeting', 'fullscreen', 'hangup', 'profile',  'sharedvideo', 'settings', 'raisehand',
                        'videoquality', 'stats', 'shortcuts', 'filmstrip', 'tileview'
                    ]; // Viewer-level access
                    const buttonsParticipant = ['microphone', 'camera', 'desktop', 'fodeviceselection'];
                    const buttonsModerator = ['mute-everyone', 'recording', 'livestreaming', 'etherpad'];
                    const configDict = {
                        disableInviteFunctions: true,
                        subject: " ", // Override roomName display
                        dropbox: false, // Disable the Dropbox recording option
                        fileRecordingsServiceSharingEnabled: false, // Don't show users popup with initial recording location?
                    }

                    if (this.isViewer) {
                        configDict['startWithAudioMuted'] = true;
                        configDict['startWithVideoMuted'] = true;
                        // Stop viewers re-enabling mic during call
                        configDict['disableShortcuts'] = true;
                        configDict['notifications'] = notificationsView;
                    } else {
                        buttons.push(...buttonsParticipant);
                    }
                    if (this.isModerator) {
                        buttons.push(...buttonsModerator);
                    }

                    const options = {
                        roomName: roomName,
                        // width: 700,
                        // height: 700,
                        height: '100%',
                        parentNode: document.getElementById('videoJitsiFrame'),
                        // parentNode: this.$el,
                        jwt: jwt,
                        configOverwrite: configDict,
                        interfaceConfigOverwrite: {
                            HIDE_INVITE_MORE_HEADER: true, // When solo in meeting
                            TOOLBAR_BUTTONS: buttons,
                        }
                    };
                    this.isStarted = true;
                    console.log("jitsiOptions:", options);

                    this.jitsiAPI = new window.JitsiMeetExternalAPI(domain, options);
                    this.jitsiAPI.addListener('videoConferenceJoined', this.doVideoConferenceJoined);
                    this.jitsiAPI.addListener('videoConferenceLeft', this.doVideoConferenceLeft);
                    this.jitsiAPI.addListener('participantKickedOut', this.doParticipantKickedOut);

                    // this.jitsiAPI.addListener('participantRoleChanged', this.doParticipantRoleChanged); // NOT useful
                    // this.jitsiAPI.addListener('moderationStatusChanged', this.doUnknownEvent)
                    // this.jitsiAPI.addListener('moderationParticipantApproved', this.doUnknownEvent)
                    // this.jitsiAPI.addListener('moderationParticipantRejected', this.doUnknownEvent)
                    // this.jitsiAPI.addListener('recordingLinkAvailable', this.doUnknownEvent)
                })
                .catch(tokenErr => {
                    this.error = tokenErr;
                });

        },
        doVideoConferenceJoined(obj) {
            console.log('Joined conference:', obj);
            this.jitsiConnectionId = obj.id;
            EventService.postJitsiBrowserHook('videoConferenceJoined', obj);
        },
        doVideoConferenceLeft(obj) {
            console.log("Left conference:", obj);
            obj.id = this.jitsiConnectionId; // Inject our id so we know which connection to mark as closed
            EventService.postJitsiBrowserHook('videoConferenceLeft', obj);

            this.jitsiAPI.dispose();
            // const frame = document.getElementById('jitsiConferenceFrame0');
            // frame.remove();

            this.isStarted = false;
            this.isModerator = false;
            this.isViewer = false;
            this.$emit('closed');
        },
        doParticipantKickedOut(obj) {
            // If kicked.local, confess I've been booted out
            // https://jitsi.github.io/handbook/docs/dev-guide/dev-guide-iframe#participantkickedout
            /* {
                kicked: {
                    id: string, // the id of the participant removed from the room
                    local: boolean // whether or not the participant is the local particiapnt
                },
                kicker: {
                    id: string // the id of the participant who kicked out the other participant
                }

                roomName: string // ADDED HERE
                id: string // ADDED HERE
            }
            */
            console.log("Kicked:", obj);
            if (obj.kicked.local) {
                obj['roomName'] = this.room; // Add the resource_name, because it's not included
                obj.id = this.jitsiConnectionId; // Add id to obj root, so that we have have consistent data format
                EventService.postJitsiBrowserHook('participantKickedOut', obj);
            }
        },
        // doParticipantRoleChanged(obj) {
        //     // SAMPLE OBJECT NOT USEFUL :-(
        //     // Role changed: {id: '0bb98edc', role: 'moderator'}

        //     console.log("Role changed:", obj);
        //     // If role -> moderator : Add video+audio controls
        //     // If role -> participant : Remove video+audio?
        //     // Updating UI once live may not be possible?
        // },
        // doUnknownEvent(obj) {
        //     console.log("====================================");
        //     console.log("Obj:", obj);
        // },
        doToggleFullScreen() {
            this.isFullScreen = !this.isFullScreen;
        }
    },
    mounted() {
        if (this.start_now) {
            this.startVideo();
        }
    },
}
</script>

<style>
.video-jitsi {
  align-items: center;
  background-color: #333;
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-height: 300px;
}
.video-jitsi iframe {
    flex-grow: 1;
}
.video-jitsi-started .video-jitsi {
    min-height: 700px;
}
/*
.jitsi-moderator {
  border: 2px solid red;
}
.jitsi-viewer {
  border: 2px solid grey;
}
*/
.jitsi-note {
  align-self: stretch;
  color: #fff;
  font-size: 13px;
  order: 5;
  padding: 5px 10px;
  text-align: right;
}
.jitsi-note-moderator {
  /* background-color: black; */
  background-color: inherit;
}
.jitsi-note-viewer {
  /* background-color: grey; */
  background-color: inherit;
}
.video-jitsi-error {
  margin: 10px;
}
.video-jitsi-fullscreen {
    display: flex;
    position: absolute;
    flex-direction:column;
    height: 100vh;
    width: calc(100vw - 300px);
    top: 0;
    left: 0;
}
.video-jitsi-fullscreen .video-jitsi {
    flex-grow: 1;
    padding-top: 40px;
}
.video-jitsi-footer {
    display: flex;
    align-items: stretch;
    flex-direction: row-reverse;
    justify-content: space-between;
    background-color: #222;
    color: #fff;
    text-align: right;
    padding: 4px 10px;
}
</style>